import { toast } from "@/components/common/Toast/Toast"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { X } from "lucide-react"
import { useState, type ReactNode } from "react"
import {
  useRecordOverride,
  useReviewStatement,
} from "../../../../../api/declarationsApi"
import {
  overrideRefusalKey,
  statementStoreAddress,
} from "../../studio/overrides"

export interface RemoveStatementControlProps {
  declId: string
  statementId: string
  framework: string
  groupIndex: number
  /** Seeds the reason field. The reviewer still confirms or edits it before
   *  submitting, so the audit trail stays human-confirmed rather than
   *  machine-authored. Used by `ArticleSlot`'s zero-purchase gesture, which
   *  opens this same dialog with a reason already written. Defaults to "". */
  initialComment?: string
  /** Replaces the default X-icon trigger with caller-supplied markup; the
   *  function argument opens the dialog. Used by `ArticleSlot` so its own
   *  labeled button drives this exact two-write flow instead of duplicating
   *  it. `DialogTrigger`'s `asChild` cannot wrap a `TooltipProvider` (it
   *  clones props onto its immediate child, which must mount a single DOM
   *  node), so a custom trigger is rendered OUTSIDE the `Dialog` and opens it
   *  by calling back rather than by composition. */
  renderTrigger?: (open: () => void) => ReactNode
}

/**
 * "Aussage entfernen": the X button (or a caller's own `renderTrigger`)
 * opens a compact reason dialog (the comment is server-MANDATORY,
 * `overrides.ts`'s own guard). On submit, TWO writes, in the SAME order v1's
 * StudioScreenA uses for exactly this gesture (`onExclude`) and for the same
 * reason: the override note is the write the server can REFUSE (422
 * `OVERRIDE_COMMENT_REQUIRED`), so it goes FIRST — a refused override leaves
 * the statement untouched, never excluded with no note explaining why (the
 * half-written state v1's own `halfWritten` prop exists to apologise for).
 * Only once the note is safely recorded does the reject fire, which is the
 * actual exclusion (the staged row's status — what `approve()` reads).
 *
 * The address is `statementStoreAddress(framework, groupIndex)` — the WIRE
 * address (`overrides.ts`'s `OverrideAddress` is a different, UI-local
 * space) — reused, not reimplemented, so a v1 surface reading the same
 * statement's override note (`entry.override`) agrees with this one.
 */
export function RemoveStatementControl({
  declId,
  statementId,
  framework,
  groupIndex,
  initialComment = "",
  renderTrigger,
}: RemoveStatementControlProps) {
  const { translate } = useTranslation()
  const recordOverride = useRecordOverride()
  const reviewStatement = useReviewStatement()
  const [open, setOpen] = useState(false)
  const [comment, setComment] = useState(initialComment)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await recordOverride.mutateAsync({
        declarationId: declId,
        address: statementStoreAddress(framework, groupIndex),
        kind: "excluded",
        comment: comment.trim(),
      })
    } catch (e) {
      setBusy(false)
      setError(
        translate(overrideRefusalKey(e) ?? "MC_DECL_V2_UNIT_REMOVE_FAILED"),
      )
      return
    }
    try {
      await reviewStatement.mutateAsync({
        declarationId: declId,
        statementId,
        action: "reject",
      })
    } catch {
      setBusy(false)
      setError(translate("MC_DECL_V2_UNIT_REMOVE_FAILED"))
      return
    }
    setBusy(false)
    setOpen(false)
    setComment(initialComment)
    toast.success(translate("MC_DECL_V2_UNIT_REMOVE_SUCCESS"))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setComment(initialComment)
          setError(null)
        }
      }}
    >
      {renderTrigger ? (
        renderTrigger(() => setOpen(true))
      ) : (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger asChild>
                <button
                  type="button"
                  aria-label={translate("MC_DECL_V2_UNIT_REMOVE")}
                  className="sv2-exdel"
                >
                  <X className="sv2-ic" />
                </button>
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">
              {translate("MC_DECL_V2_UNIT_REMOVE")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {translate("MC_DECL_V2_UNIT_REMOVE_DIALOG_TITLE")}
          </DialogTitle>
        </DialogHeader>
        <Textarea
          rows={3}
          autoFocus
          value={comment}
          placeholder={translate("MC_DECL_V2_UNIT_REMOVE_REASON_PLACEHOLDER")}
          onChange={(e) => setComment(e.target.value)}
        />
        {error && <p className="text-[12px] text-destructive">{error}</p>}
        <DialogFooter>
          <button
            type="button"
            disabled={busy}
            onClick={() => setOpen(false)}
            className="rounded px-3 py-1.5 text-[12px] text-grey5 hover:text-white"
          >
            {translate("MC_DECL_V2_UNIT_REMOVE_CANCEL")}
          </button>
          <button
            type="button"
            disabled={busy || comment.trim().length === 0}
            onClick={submit}
            className="rounded bg-destructive px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-50"
          >
            {busy
              ? translate("MC_DECL_V2_UNIT_REMOVE_RECORDING")
              : translate("MC_DECL_V2_UNIT_REMOVE_CONFIRM")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
